import { alertController, loadingController, toastController } from '@ionic/core/components';

/** Helpers de interface: toast, loading, confirmação e erros de campo. */

export async function toast(message, color = 'dark', duration = 2500) {
  const t = await toastController.create({ message, color, duration, position: 'top', buttons: [{ text: 'OK', role: 'cancel' }] });
  await t.present();
}

export const toastError = (message) => toast(message, 'danger', 4000);
export const toastSuccess = (message) => toast(message, 'success');

/** Executa fn mostrando um loading. */
export async function withLoading(message, fn) {
  const l = await loadingController.create({ message, spinner: 'crescent' });
  await l.present();
  try {
    return await fn();
  } finally {
    await l.dismiss();
  }
}

/** Diálogo de confirmação. Resolve true/false. */
export async function confirm({ header, message, confirmText = 'Confirmar', cancelText = 'Cancelar', destructive = false }) {
  const a = await alertController.create({
    header,
    message,
    buttons: [
      { text: cancelText, role: 'cancel' },
      { text: confirmText, role: 'confirm', cssClass: destructive ? 'alert-button-danger' : undefined },
    ],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  return role === 'confirm';
}

/**
 * Diálogo com um campo de texto. Resolve o texto (trim) ou null se cancelar.
 * uppercase: converte enquanto digita (pt-BR).
 */
export async function prompt({ header, message, value = '', placeholder = '', maxlength = 100, confirmText = 'Salvar', cancelText = 'Cancelar', uppercase = false }) {
  const a = await alertController.create({
    header,
    message,
    inputs: [{ name: 'value', type: 'text', value, placeholder, attributes: { maxlength, autocapitalize: uppercase ? 'characters' : 'sentences' } }],
    buttons: [
      { text: cancelText, role: 'cancel' },
      { text: confirmText, role: 'confirm' },
    ],
  });
  await a.present();
  const input = a.querySelector('input');
  if (uppercase && input) input.addEventListener('input', () => (input.value = input.value.toLocaleUpperCase('pt-BR')));
  input?.focus();
  const { role, data } = await a.onDidDismiss();
  if (role !== 'confirm') return null;
  const out = String(data?.values?.value ?? '').trim();
  return uppercase ? out.toLocaleUpperCase('pt-BR') : out;
}

/** Mostra erro num ion-input (texto vermelho abaixo do campo). */
export function setFieldError(input, message) {
  if (!input) return;
  input.errorText = message;
  input.classList.add('ion-invalid', 'ion-touched');
  input.classList.remove('ion-valid');
}

export function clearFieldError(input) {
  if (!input) return;
  input.classList.remove('ion-invalid', 'ion-touched');
}

/** Limpa o erro do campo assim que o usuário digita. */
export function autoClearErrors(root) {
  root.querySelectorAll('ion-input').forEach((el) => el.addEventListener('ionInput', () => clearFieldError(el)));
}

/**
 * Aplica os erros de validação da API ({ details: [{ path, message }] }) nos campos.
 * Retorna true se algum erro foi mostrado em campo.
 */
export function applyApiFieldErrors(root, details) {
  if (!Array.isArray(details)) return false;
  let shown = false;
  for (const d of details) {
    const input = root.querySelector(`ion-input[name="${CSS.escape(d.path)}"]`);
    if (input) {
      setFieldError(input, d.message);
      shown = true;
    }
  }
  return shown;
}

/** Valor de um ion-input já sem espaços nas pontas. */
export const valueOf = (root, name) => String(root.querySelector(`ion-input[name="${name}"]`)?.value ?? '').trim();

/** Botão de submit com estado de carregando. */
export async function busy(button, fn) {
  button.disabled = true;
  try {
    return await fn();
  } finally {
    button.disabled = false;
  }
}

/** Foto em tela cheia (toque ou "voltar" do teclado fecha). Não usa window.open: no app abriria o navegador. */
export function openPhoto(url) {
  const el = document.createElement('div');
  el.className = 'photo-viewer';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Foto da peça');
  const img = document.createElement('img');
  img.src = url;
  img.alt = 'Foto da peça';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'photo-viewer-x';
  close.setAttribute('aria-label', 'Fechar');
  close.textContent = '×';
  el.append(img, close);
  const done = () => {
    el.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e) => e.key === 'Escape' && done();
  el.addEventListener('click', done);
  document.addEventListener('keydown', onKey);
  document.body.append(el);
}
